import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import cls from 'classnames/bind';

import isNumber from 'lodash/isNumber';
import padStart from 'lodash/padStart';

import { l10n } from '@ott/l10n';
import Tooltip from '@ott/tooltip';

import formatDatepickerPrice from '../../../../../../helpers/formatDatepickerPrice';

import s from './Day.scss';

const cx = cls.bind(s);

class Day extends PureComponent {
  static propTypes = {
    isSelected: PropTypes.bool,
    isInSelectedRange: PropTypes.bool,
    isHovered: PropTypes.bool,
    isInHoveredRange: PropTypes.bool,
    hasLeftRounding: PropTypes.bool,
    hasRightRounding: PropTypes.bool,
    isWeekend: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isHidden: PropTypes.bool, // скрытые даты предыдущего месяца
    isEndDate: PropTypes.bool,

    year: PropTypes.number,
    month: PropTypes.number,
    day: PropTypes.number,

    maxDays: PropTypes.number,
    shouldShowMaxDaysTooltip: PropTypes.bool,
    maxDaysTooltipText: PropTypes.string,

    heatmap: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      min: PropTypes.number.isRequired,
      mid: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
      time: PropTypes.number.isRequired,
      prices: PropTypes.PropTypes.object.isRequired,
    }),
    heatmapStatus: PropTypes.shape({
      initial: PropTypes.bool.isRequired,
      loading: PropTypes.bool.isRequired,
      success: PropTypes.bool.isRequired,
      error: PropTypes.bool.isRequired,
      cancelled: PropTypes.bool.isRequired,
    }),
    withHeatmap: PropTypes.bool,
    showHeatmapPrices: PropTypes.bool,
    onDateHover: PropTypes.func,
    onDateSelect: PropTypes.func,
  };

  constructor(props, ctx) {
    super(props, ctx);
    this.handleHover = this.handleHover.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  getCurrentDate() {
    const { year, month, day } = this.props;
    return new Date(year, month, day);
  }

  getActiveDate() {
    const { isDisabled, isHidden } = this.props;

    if (!isHidden && !isDisabled) {
      return this.getCurrentDate();
    }
  }

  handleHover() {
    const { onDateHover } = this.props;
    const activeDate = this.getActiveDate();
    activeDate && onDateHover(activeDate);
  }

  handleClick() {
    const { onDateSelect } = this.props;
    const activeDate = this.getActiveDate();

    if (activeDate) {
      onDateSelect(activeDate);
    }
  }

  getHeatmapPriceKey() {
    const { day, month, year } = this.props;
    return `${year}${padStart(month + 1, 2, '0')}${padStart(day, 2, '0')}`;
  }

  getHeatmapPrice() {
    const { heatmap } = this.props;

    if (!heatmap || !heatmap.prices) {
      return false;
    }

    const heatmapPriceKey = this.getHeatmapPriceKey();

    return heatmap.prices[heatmapPriceKey];
  }

  checkMinHeatmapValue = (heatmapPrice) => {
    if (!heatmapPrice) {
      return false;
    }

    const { heatmap } = this.props;

    return heatmapPrice.price === heatmap.min;
  };

  getHeatmapRangeValue(heatmapPrice) {
    if (!heatmapPrice) {
      return null;
    }

    return heatmapPrice.range;
  }

  render() {
    const {
      day,
      month,
      year,
      isHoliday,
      heatmap,
      heatmapStatus,
      withHeatmap,
      showHeatmapPrices,
      isDisabled,
      isHidden,
      isSelected,
      isInSelectedRange,
      isHovered,
      isInHoveredRange,
      hasLeftRounding,
      hasRightRounding,
      isWeekend,
      maxDays,
      shouldShowMaxDaysTooltip,
      maxDaysTooltipText,
      isDoubleSelected,
    } = this.props;

    const loading = heatmapStatus?.loading;
    const currency = heatmap?.currency;
    const heatmapPrice = this.getHeatmapPrice();
    const heatmapPriceValue = heatmapPrice?.price;
    const heatmapRangeValue = this.getHeatmapRangeValue(heatmapPrice);

    const withHeatmapInfo = withHeatmap && !isDisabled && !isHidden;
    const showHeatmapValue = withHeatmapInfo && isNumber(heatmapRangeValue);
    const showPrice = withHeatmapInfo && showHeatmapPrices;
    const showPoint = withHeatmapInfo && !showHeatmapPrices;
    const isMinHeatmapValue = withHeatmapInfo && this.checkMinHeatmapValue(heatmapPrice);
    let heatmapValueProps = heatmapRangeValue;

    if (heatmapValueProps === 1 && isSelected) {
      heatmapValueProps = 3;
    }

    const Wrapper = shouldShowMaxDaysTooltip ? Tooltip : React.Fragment;

    const wrapperProps =
      Wrapper === Tooltip
        ? {
            content: maxDaysTooltipText || l10n('datepicker.outOfRangeTooltip', { value: maxDays }),
            triggerClassName: s.tooltip,
            className: s.tooltipContent,
            triggerMode: 'click',
          }
        : {};

    return (
      <Wrapper {...wrapperProps}>
        <div
          className={cx('Day', {
            'Day--isSelected': isSelected,
            'Day--isInSelectedRange': isInSelectedRange,
            'Day--isHovered': isHovered,
            'Day--isInHoveredRange': isInHoveredRange,
            'Day--hasLeftRounding': hasLeftRounding,
            'Day--hasRightRounding': hasRightRounding,
            'Day--isWeekend': isWeekend,
            'Day--isDisabled': isDisabled,
            'Day--isHidden': isHidden,
            'Day--isHoliday': isHoliday,
            'Day--isDoubleSelected': isDoubleSelected,
          })}
          onMouseEnter={this.handleHover}
          onClick={this.handleClick}
          data-locator={`${padStart(day, 2, '0')}${padStart(month + 1, 2, '0')}${year}`}
        >
          <div
            className={cx('inner', {
              'inner--hasBorderOnHover': !showHeatmapValue,
            })}
          >
            <span
              className={cx('dayNumber', {
                'dayNumber--withHeatmap': withHeatmap,
              })}
            >
              {day}
            </span>
            {showPoint &&
              (isMinHeatmapValue ? (
                <div className={cx('minValue')}>MIN</div>
              ) : (
                <div
                  className={cx('point', {
                    [`point--heatmapValue--${heatmapValueProps}`]: showHeatmapValue,
                    [`point--appear`]: showHeatmapValue,
                  })}
                />
              ))}
            {showPrice &&
              (loading ? (
                <div className={cx('point', 'point--appear')} />
              ) : (
                <span
                  className={cx('price', {
                    [`price--heatmapValue--${heatmapValueProps}`]: showHeatmapValue,
                  })}
                >
                  {formatDatepickerPrice(heatmapPriceValue, currency)}
                </span>
              ))}
          </div>
        </div>
      </Wrapper>
    );
  }
}

export default Day;
